import { useMemo, useState } from 'react';
import type { MhProduct } from '@/types/mh';
import { formatDetailContent, parseProductFiles, type ProductFileItem } from '@/utils/product-display';
import ReviewList from './ReviewList';

type TabKey = 'detail' | 'images' | 'reviews';

const FEATURES = [
  { title: '单次服务', desc: '按需付费，灵活使用' },
  { title: '专业可靠', desc: '由专业团队提供技术支持' },
  { title: '安全保密', desc: '严格的数据保护和隐私政策' },
];

const ADVANTAGES = [
  '专业团队提供技术支持',
  '多重数据源保证准确性',
  '严格的隐私保护措施',
  '快速响应用户需求',
  '7×24小时在线服务',
  '查询失败全额退款',
];

const STEPS = [
  '选择合适的查询套餐',
  '填写准确的查询信息',
  '提交查询请求并完成支付',
  '等待系统处理查询',
  '查看查询结果并下载报告',
];

type DetailTabsProps = {
  product: MhProduct;
};

function fileIcon(file: ProductFileItem): string {
  if (file.isPdf) {
    return 'ri-file-pdf-2-line';
  }
  if (file.ext === 'doc' || file.ext === 'docx') {
    return 'ri-file-word-2-line';
  }
  return 'ri-file-download-line';
}

function FileCard({ file, index }: { file: ProductFileItem; index: number }) {
  return (
    <div className="rounded-xl overflow-hidden bg-gray-50 border border-gray-100">
      <div className="relative h-48 bg-gray-100 flex items-center justify-center">
        {file.isImage ? (
          <img src={file.url} alt={file.name} className="w-full h-full object-cover object-top" />
        ) : file.isPdf ? (
          <iframe src={file.url} title={file.name} className="w-full h-full bg-white" />
        ) : (
          <div className="flex flex-col items-center text-gray-400">
            <i className={`${fileIcon(file)} text-5xl mb-2`} />
            <span className="text-xs uppercase">{file.ext || 'file'}</span>
          </div>
        )}
        <div className="absolute top-2 left-2 w-6 h-6 flex items-center justify-center bg-black/50 text-white text-xs rounded-full">
          {index + 1}
        </div>
      </div>
      <div className="p-3">
        <p className="text-sm font-medium text-gray-700 truncate mb-2" title={file.name}>
          {file.name}
        </p>
        <div className="flex gap-3 text-xs">
          <a href={file.url} target="_blank" rel="noreferrer" className="text-gray-700 hover:text-gray-900">
            预览
          </a>
          <a href={file.url} download={file.name} className="text-gray-400 hover:text-gray-700">
            下载
          </a>
        </div>
      </div>
    </div>
  );
}

export default function DetailTabs({ product }: DetailTabsProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('images');
  const [expanded, setExpanded] = useState(false);
  const fileItems = useMemo(() => parseProductFiles(product.caseFiles), [product.caseFiles]);
  const detailHtml = useMemo(
    () => formatDetailContent(product.detailContent || product.description),
    [product.detailContent, product.description],
  );
  const tabs: { key: TabKey; label: string; badge?: string }[] = [
    { key: 'images', label: '案例展示', badge: String(fileItems.length) },
    { key: 'detail', label: '详细描述' },
    { key: 'reviews', label: '用户评价', badge: '99+' },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-100">
      {/* Tab header */}
      <div className="border-b border-gray-100">
        <nav className="flex space-x-6 px-6 pt-5">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center space-x-1.5 pb-3 border-b-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              <span>{tab.label}</span>
              {tab.badge && (
                <span className="bg-gray-100 text-gray-500 text-xs px-1.5 py-0.5 rounded-full">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {/* Images tab */}
        {activeTab === 'images' && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-900">服务详情</h2>
              <button
                onClick={() => setExpanded((v) => !v)}
                className="text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                {expanded ? '收起' : '展开全部'}
              </button>
            </div>
            <div
              className={`product-detail-content text-gray-600 text-sm leading-relaxed overflow-hidden transition-all duration-300 ${
                expanded ? '' : 'max-h-48'
              }`}
              dangerouslySetInnerHTML={{
                __html: detailHtml || '<p class="text-gray-400">暂无服务详情，请联系客服询问详情。</p>',
              }}
            />

            <div className="mt-7">
              <h3 className="text-base font-semibold text-gray-900 mb-4">案例展示</h3>
              {fileItems.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {fileItems.map((file, i) => (
                    <FileCard key={`${file.raw}-${i}`} file={file} index={i} />
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 py-12 text-center text-sm text-gray-400">
                  暂无案例文件
                </div>
              )}
            </div>
          </div>
        )}

        {/* Detail tab */}
        {activeTab === 'detail' && (
          <div>
            {/* Features */}
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-4">服务特色</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {FEATURES.map((f) => (
                  <div key={f.title} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-xl">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-green-500 text-base" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-0.5">{f.title}</h4>
                      <p className="text-xs text-gray-500">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Advantages */}
            <div className="mt-7">
              <h3 className="text-base font-semibold text-gray-900 mb-4">服务优势</h3>
              <div className="space-y-2.5">
                {ADVANTAGES.map((adv) => (
                  <div key={adv} className="flex items-center space-x-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-green-500 text-base" />
                    </div>
                    <span className="text-sm text-gray-700">{adv}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Steps */}
            <div className="mt-7">
              <h3 className="text-base font-semibold text-gray-900 mb-4">使用说明</h3>
              <div className="space-y-3">
                {STEPS.map((step, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-gray-100 text-gray-700 rounded-full text-xs font-semibold flex-shrink-0">
                      {i + 1}
                    </div>
                    <p className="text-sm text-gray-700 flex-1 pt-0.5">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Reviews tab */}
        {activeTab === 'reviews' && <ReviewList />}
      </div>
    </div>
  );
}
